import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import CustomDataGrid from "../../Components/CustomDataGrid";
import EditIcon from "@mui/icons-material/Edit";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";
import { Lock, PersonAdd, People } from "@mui/icons-material"; // Added icons
import { useLang } from "../../context/LanguageContext"; // Import useLang

const defaultBooleans = {
    isAddDialogOpen: false,
    isEditDialogOpen: false,
};

const UsersPage = () => {
    const { t, lang } = useLang(); // Use translation hook
    const authHeader = useAuthHeader();
    const [booleans, setBooleans] = useState(defaultBooleans);
    const [selectedUser, setSelectedUser] = useState(null);
    const [availableEmployeeTypes, setAvailableEmployeeTypes] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(0);
    const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] =
        useState(false);
    const [changePasswordData, setChangePasswordData] = useState({
        password: "",
        confirmPassword: "",
    });

    const [users, setUsers] = useState([]);
    const columns = [
        { field: "id", headerName: t("USER_ID") || "ID", width: 90 },
        {
            field: "name",
            headerName: t("NAME"),
            width: 150,
        },
        {
            field: "username",
            headerName: t("USERNAME"),
            width: 150,
        },
        {
            field: "employeeType",
            headerName: t("TYPE"),
            width: 150,
            valueGetter: (value) => {
                return value.name;
            },
        },
        {
            field: "active",
            headerName: t("USER_ACTIVE"),
            width: 150,
            type: "boolean",
        },
        {
            field: "createdAt",
            headerName: t("CREATED_AT"),
            width: 200,
            valueGetter: (value) => {
                return new Date(value).toLocaleString();
            },
        },
    ];

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/users`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: authHeader,
                        },
                    },
                );

                const data = await response.json();
                if (response.ok) {
                    setUsers(data);
                }
            } catch (err) {
                console.log(err);
            }
        };

        fetchUsers();
    }, [authHeader]);

    const actions = [
        {
            title: t("EDIT_USER"),
            color: "primary",
            handler: (id) => {
                setSelectedUserId(id);
                setBooleans({ ...booleans, isEditDialogOpen: true });
            },
            parameters: ["id"],
            icon: <EditIcon />,
        },
        {
            title: t("CHANGE_PASSWORD"),
            color: "secondary",
            handler: (id) => {
                setSelectedUserId(id);
                setIsChangePasswordDialogOpen(true);
            },
            parameters: ["id"],
            icon: <Lock />,
        },
    ];

    const handleSubmit = async () => {
        try {
            let response = null;

            if (booleans.isAddDialogOpen) {
                response = await fetch(`${REACT_APP_BACKEND_URL}/users`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authHeader,
                    },
                    body: JSON.stringify(selectedUser),
                });
            } else {
                response = await fetch(
                    `${REACT_APP_BACKEND_URL}/users/${selectedUser.id}`,
                    {
                        method: "PATCH",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: authHeader,
                        },
                        body: JSON.stringify(selectedUser),
                    },
                );
            }

            const data = await response.json();
            if (response.ok) {
                setUsers(data);
                setBooleans(defaultBooleans);
                setAvailableEmployeeTypes([]);
                setSelectedUser(null);
                setSelectedUserId(0);
            }
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        const getUserToEdit = async () => {
            const response = await fetch(
                `${REACT_APP_BACKEND_URL}/USERS/${selectedUserId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: authHeader,
                    },
                },
            );

            const data = await response.json();
            if (response.ok) {
                setAvailableEmployeeTypes(data.employeeTypes);
                setSelectedUser(data.user);
            }
        };
        // Also fetch types if adding new user (need a separate endpoint to just get types usually,
        // but here it seems we misuse the edit endpoint or maybe fetch types on mount?
        // The original code fetched types inside getUserToEdit which is called when dialog opens.
        // But for "Add", selectedUserId is 0. Does backend handle ID 0?
        // Original code: if (booleans.isEditDialogOpen || booleans.isAddDialogOpen) { getUserToEdit(); }
        // If ID is 0, backend likely returns just types and empty user?
        // Let's keep behavior same.
        if (booleans.isEditDialogOpen || booleans.isAddDialogOpen) {
            getUserToEdit();
        }
    }, [
        booleans.isEditDialogOpen,
        selectedUserId,
        authHeader,
        booleans.isAddDialogOpen,
    ]);

    // Helper to render form fields
    const renderFormFields = () => (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "15px",
                py: "10px",
                direction: lang === "ar" ? "rtl" : "ltr",
            }}
        >
            <TextField
                label={t("NAME")}
                value={selectedUser?.name || ""}
                onChange={(e) =>
                    setSelectedUser({
                        ...selectedUser,
                        name: e.target.value,
                    })
                }
                size="small"
                fullWidth
            />
            <TextField
                label={t("USERNAME")}
                value={selectedUser?.username || ""}
                onChange={(e) =>
                    setSelectedUser({
                        ...selectedUser,
                        username: e.target.value,
                    })
                }
                size="small"
                fullWidth
            />
            {/* Password field only for Add dialog, or if logic allows editing */}
            {booleans.isAddDialogOpen && (
                <>
                    <TextField
                        label={t("PASSWORD")}
                        type="password"
                        size="small"
                        onChange={(e) =>
                            setSelectedUser({
                                ...selectedUser,
                                password: e.target.value,
                            })
                        }
                        value={selectedUser?.password || ""}
                        fullWidth
                    />
                    <TextField
                        label={t("CONFIRM_PASSWORD")}
                        type="password"
                        size="small"
                        onChange={(e) =>
                            setSelectedUser({
                                ...selectedUser,
                                confirmPassword: e.target.value,
                            })
                        }
                        value={selectedUser?.confirmPassword || ""}
                        fullWidth
                    />
                </>
            )}

            {/* In Edit mode, show password placeholder or hidden */}
            {booleans.isEditDialogOpen && (
                <TextField
                    label={t("PASSWORD")}
                    value="********"
                    disabled
                    size="small"
                    fullWidth
                    helperText={
                        t("USE_CHANGE_PASSWORD_BTN") ||
                        "Use change password button to update"
                    }
                />
            )}

            <Select
                value={
                    selectedUser?.employeeTypeId ||
                    selectedUser?.employeeType?.id ||
                    ""
                }
                size="small"
                displayEmpty
                onChange={(e) =>
                    setSelectedUser({
                        ...selectedUser,
                        employeeTypeId: e.target.value,
                        // Update nested object for immediate UI feedback if needed,
                        // though normally we send ID to backend
                        employeeType: { id: e.target.value },
                    })
                }
                fullWidth
            >
                <MenuItem value="" disabled>
                    {t("SELECT_USER_TYPE") || "Select Type"}
                </MenuItem>
                {availableEmployeeTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                        {type.name}
                    </MenuItem>
                ))}
            </Select>

            <FormGroup>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!selectedUser?.active}
                            onChange={(e) =>
                                setSelectedUser({
                                    ...selectedUser,
                                    active: e.target.checked,
                                })
                            }
                        />
                    }
                    label={t("IS_ACTIVE")}
                />
            </FormGroup>
        </Box>
    );

    return (
        <Box sx={{ width: "100%", p: 3 }}>
            {/* Gradient Header */}
            <Paper
                elevation={0}
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 4,
                    p: 2.5,
                    borderRadius: 3,
                    background:
                        "linear-gradient(135deg, #6a1b9a 0%, #4a148c 100%)", // Purple gradient for Users
                    color: "white",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <People sx={{ fontSize: 32, opacity: 0.9 }} />
                    <Typography variant="h5" fontWeight={700}>
                        {t("EMPLOYEES")}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<PersonAdd />}
                    onClick={() =>
                        setBooleans({ ...booleans, isAddDialogOpen: true })
                    }
                    sx={{
                        bgcolor: "rgba(255,255,255,0.2)",
                        color: "white",
                        fontWeight: 700,
                        "&:hover": { bgcolor: "rgba(255,255,255,0.35)" },
                    }}
                >
                    {t("ADD_EMPLOYEE")}
                </Button>
            </Paper>

            <CustomDataGrid
                columns={columns}
                rows={users}
                actions={actions}
                perPage={20}
                hasEdit={false}
            />

            {/* Add/Edit Dialog */}
            <Dialog
                open={booleans.isAddDialogOpen || booleans.isEditDialogOpen}
                onClose={() => {
                    setSelectedUser(null);
                    setBooleans(defaultBooleans);
                }}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {booleans.isAddDialogOpen ? t("ADD_USER") : t("EDIT_USER")}
                </DialogTitle>
                <DialogContent>{renderFormFields()}</DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setSelectedUserId(0);
                            setSelectedUser(null);
                            setBooleans(defaultBooleans);
                        }}
                        color="error"
                    >
                        {t("CANCEL")}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                    >
                        {t("CONFIRM")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Change Password Dialog */}
            <Dialog
                open={isChangePasswordDialogOpen}
                onClose={() => {
                    setIsChangePasswordDialogOpen(false);
                    setSelectedUserId(0);
                    setChangePasswordData({
                        password: "",
                        confirmPassword: "",
                    });
                }}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>{t("CHANGE_PASSWORD")}</DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "15px",
                            py: "10px",
                        }}
                    >
                        <TextField
                            label={t("PASSWORD")}
                            type="password"
                            fullWidth
                            value={changePasswordData.password}
                            onChange={(e) =>
                                setChangePasswordData((prev) => ({
                                    ...prev,
                                    password: e.target.value,
                                }))
                            }
                        />
                        <TextField
                            label={t("CONFIRM_PASSWORD")}
                            type="password"
                            fullWidth
                            value={changePasswordData.confirmPassword}
                            onChange={(e) =>
                                setChangePasswordData((prev) => ({
                                    ...prev,
                                    confirmPassword: e.target.value,
                                }))
                            }
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setIsChangePasswordDialogOpen(false);
                            setChangePasswordData({
                                password: "",
                                confirmPassword: "",
                            });
                            setSelectedUserId(0);
                        }}
                        color="error"
                    >
                        {t("CANCEL")}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={async () => {
                            if (
                                changePasswordData.password !==
                                changePasswordData.confirmPassword
                            ) {
                                // Add logic for password mismatch toast if possible, or just fail silently/log for now as per orig
                                console.log("Passwords do not match");
                                return;
                            }
                            try {
                                const response = await fetch(
                                    `${REACT_APP_BACKEND_URL}/users/${selectedUserId}/change-password`,
                                    {
                                        method: "PATCH",
                                        headers: {
                                            "Content-Type": "application/json",
                                            Authorization: authHeader,
                                        },
                                        body: JSON.stringify(
                                            changePasswordData,
                                        ),
                                    },
                                );

                                const data = await response.json();
                                if (response.ok) {
                                    setUsers(data);
                                    setIsChangePasswordDialogOpen(false);
                                    setSelectedUserId(0);
                                    setChangePasswordData({
                                        password: "",
                                        confirmPassword: "",
                                    });
                                }
                            } catch (err) {
                                console.log(err);
                            }
                        }}
                    >
                        {t("CONFIRM")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UsersPage;
